import type { ReactElement } from "react";
import { motion } from "framer-motion";
import { FontWeight } from "@/theme/typography";
import { getRadiusCssValue } from "@/theme/radius";
import { pxToRem } from "@/theme/spacing";
import { CmarkdownLabelText } from "../Cmarkdowntext/CmarkdownLabelText";
import type { KanbanCard as KanbanCardModel } from "./parse-mermaid-kanban";
import { KanbanCardBadgeList } from "./KanbanCardBadgeList";
import { KanbanCardTooltip } from "./KanbanCardTooltip";
import { getMetadataEntries, getPriorityAccentColor, isBadgeMetadataEntry, } from "./kanban-selectors";
import { CARD_BACKGROUND_HOVER, CARD_BACKGROUND_REST, CARD_BORDER_HOVER, CARD_BORDER_REST, CARD_TRANSITION, WEBKIT_BOX_ORIENT_VERTICAL, } from "./kanban-view-constants";
import { DIMMED_OPACITY } from "./kanban-constants";
interface KanbanCardProps {
    card: KanbanCardModel;
    cardKey: string;
    columnIndex: number;
    cardIndex: number;
    isHovered: boolean;
    isDimmed: boolean;
    onHoverCard: (cardKey: string) => void;
    clearHoveredCard: () => void;
}
export function KanbanCard({ card, cardKey, columnIndex, cardIndex, isHovered, isDimmed, onHoverCard, clearHoveredCard, }: KanbanCardProps): ReactElement {
    const metadataEntries = getMetadataEntries(card.metadata);
    const metadataBadges = metadataEntries.filter(isBadgeMetadataEntry);
    const priorityAccent = getPriorityAccentColor(card.metadata.priority);
    return (
      <motion.div data-el="Cmarkdownkanban/Card/Entry" key={cardKey} onMouseEnter={() => onHoverCard(cardKey)} onMouseLeave={clearHoveredCard} onPointerLeave={clearHoveredCard} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{
            duration: 0.22,
            delay: columnIndex * 0.06 + 0.08 + cardIndex * 0.03,
            ease: "easeOut",
        }} style={{
            position: "relative",
            opacity: isDimmed ? DIMMED_OPACITY : 1,
            transition: CARD_TRANSITION,
            borderRadius: getRadiusCssValue(9),
        }}>
      <div style={{
            width: "100%",
            cursor: "default",
            display: "flex",
            flexDirection: "column",
            gap: pxToRem(8),
            padding: `${pxToRem(11)} ${pxToRem(13)}`,
            borderRadius: getRadiusCssValue(9),
            backgroundImage: isHovered ? CARD_BACKGROUND_HOVER : CARD_BACKGROUND_REST,
            boxShadow: isHovered ? CARD_BORDER_HOVER : CARD_BORDER_REST,
            fontSize: pxToRem(13),
            color: "rgba(255,255,255,0.88)",
            transition: CARD_TRANSITION,
        }}>
        <div style={{
            display: "flex",
            alignItems: "flex-start",
            gap: pxToRem(8),
        }}>
            {priorityAccent ? (<span aria-hidden="true" style={{
                marginTop: pxToRem(5),
                width: pxToRem(6),
                height: pxToRem(6),
                borderRadius: "50%",
                backgroundColor: priorityAccent,
                flexShrink: 0,
            }}/>) : null}

          <CmarkdownLabelText dataElScope="Cmarkdownkanban/KanbanCard" content={card.label} size={13} weight={FontWeight.Medium} style={{ minWidth: 0, width: "100%" }} paragraphStyle={{ marginBottom: 0, minWidth: 0, width: "100%" }} textStyle={{
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: WEBKIT_BOX_ORIENT_VERTICAL,
            overflow: "hidden",
            lineHeight: 1.35,
            color: "rgba(255,255,255,0.88)",
        }}/>
        </div>

        <KanbanCardBadgeList cardKey={cardKey} metadataBadges={metadataBadges}/>
      </div>

        <KanbanCardTooltip cardKey={cardKey} isHovered={isHovered} metadataEntries={metadataEntries}/>
      </motion.div>
    );
}
